
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type ForwardTransparentShaderUniforms = {
	uModelViewMatrix: WebGLUniformLocation,
	uProjectionMatrix: WebGLUniformLocation,
	uModelMatrix: WebGLUniformLocation,
	uBindShapeMatrix: WebGLUniformLocation,
	uMatrixSampler: WebGLUniformLocation,
	uDataSettings: WebGLUniformLocation,
	uBlendSettings: WebGLUniformLocation,
	uBoneOverrides: WebGLUniformLocation[],
	uBoneOverrideIndices: WebGLUniformLocation[],
	uBoneOverrideCount: WebGLUniformLocation,
	uBoneMatrix: WebGLUniformLocation,
	uTextureSampler: WebGLUniformLocation,
	uPresetColor: WebGLUniformLocation,
	uAmbientColor: WebGLUniformLocation,
	uCameraPosition: WebGLUniformLocation,
	uOpacity: WebGLUniformLocation,
	uVolumetricMap: WebGLUniformLocation,
	uScreenSize: WebGLUniformLocation,
	uLightDataMap: WebGLUniformLocation,
	uLightCount: WebGLUniformLocation,
	uLightDataHeight: WebGLUniformLocation,
	uTileDataMap: WebGLUniformLocation,
	uTilesX: WebGLUniformLocation,
	uTileSize: WebGLUniformLocation,
	uShadowCascadeMap0: WebGLUniformLocation,
	uShadowCascadeMap1: WebGLUniformLocation,
	uShadowCascadeMap2: WebGLUniformLocation,
	uShadowCascadeMap3: WebGLUniformLocation,
	uCascadeViewMatrix: WebGLUniformLocation[],
	uCascadeSplits: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uNormalMapSampler: WebGLUniformLocation,
	uMetallicRoughnessSampler: WebGLUniformLocation
};

export type ForwardTransparentShaderAttributes = {
	aVertexPosition: number,
	aVertexNormal: number,
	aTextureCoord: number,
	aJointWeights: number,
	aJointIndices: number,
	vWorldPosition: number,
	vWorldNormal: number,
	vTextureCoord: number
};

export type ForwardTransparentShaderSettings = ('TEXTURE' | 'ANIMATION' | 'BONE_OVERRIDES' | 'ATTACHMENT' | 'PRESET_COLOR' | 'VOLUMETRIC' | 'TILE_SKIP_SHADOWED' | 'SHADOW_CSM' | 'NORMAL_MAP' | 'METALLIC_ROUGHNESS')[];

export class ForwardTransparentShader extends ShaderBase<ForwardTransparentShaderUniforms, ForwardTransparentShaderAttributes> {

    private static fragmentShaderSource = `
in vec4 vWorldPosition;
in vec3 vWorldNormal;
out vec4 fragColor;

#ifdef NOODLE_TEXTURE
in vec2 vTextureCoord;
uniform sampler2D uTextureSampler;
#endif

#ifdef NOODLE_PRESET_COLOR
uniform vec4 uPresetColor;
#endif

uniform vec3 uAmbientColor;
uniform vec3 uCameraPosition;
uniform float uOpacity;

#ifdef NOODLE_VOLUMETRIC
uniform sampler2D uVolumetricMap;
uniform vec2 uScreenSize;
#endif

// --- begin: pbr_lighting.glsl ---
// Cook-Torrance PBR BRDF
// D: GGX/Trowbridge-Reitz normal distribution
// F: Schlick fresnel approximation
// G: Smith geometry (Schlick-GGX)

float distribution_ggx(float NdotH, float roughness) {
    float a = roughness * roughness;
    float a2 = a * a;
    float denom = NdotH * NdotH * (a2 - 1.0) + 1.0;
    return a2 / (3.14159265 * denom * denom);
}

float geometry_schlick_ggx(float NdotV, float roughness) {
    float r = roughness + 1.0;
    float k = (r * r) / 8.0;
    return NdotV / (NdotV * (1.0 - k) + k);
}

float geometry_smith(float NdotV, float NdotL, float roughness) {
    return geometry_schlick_ggx(NdotV, roughness) * geometry_schlick_ggx(NdotL, roughness);
}

vec3 fresnel_schlick(float cosTheta, vec3 F0) {
    return F0 + (1.0 - F0) * pow(clamp(1.0 - cosTheta, 0.0, 1.0), 5.0);
}

vec3 pbr_direct_lighting_ndotl(vec3 normal, vec3 surfaceToLight, vec3 surfaceToCamera, vec3 albedo, float metallic, float roughness, vec3 lightColor, float NdotL) {
    vec3 H = normalize(surfaceToLight + surfaceToCamera);

    float NdotV = max(dot(normal, surfaceToCamera), 0.001);
    float NdotH = max(dot(normal, H), 0.0);
    float HdotV = max(dot(H, surfaceToCamera), 0.0);

    // Dielectrics reflect ~4% at normal incidence; metals use their albedo as F0
    vec3 F0 = mix(vec3(0.04), albedo, metallic);

    float D = distribution_ggx(NdotH, roughness);
    float G = geometry_smith(NdotV, NdotL, roughness);
    vec3 F = fresnel_schlick(HdotV, F0);

    // Specular: Cook-Torrance
    vec3 specular = (D * G * F) / max(4.0 * NdotV * NdotL, 0.001);

    // Diffuse: energy-conserving Lambert (metals have no diffuse)
    // Energy conservation: metals have no diffuse, dielectrics scale by (1 - F).
    // PI divisor intentionally omitted — standard game engine simplification to
    // avoid dimming the scene and re-tuning all light intensities.
    vec3 kD = (vec3(1.0) - F) * (1.0 - metallic);
    vec3 diffuse = kD * albedo;

    return (diffuse + specular) * lightColor * NdotL;
}

vec3 pbr_direct_lighting(vec3 normal, vec3 surfaceToLight, vec3 surfaceToCamera, vec3 albedo, float metallic, float roughness, vec3 lightColor) {
    float NdotL = max(dot(normal, surfaceToLight), 0.0);
    return pbr_direct_lighting_ndotl(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, lightColor, NdotL);
}

// --- end: pbr_lighting.glsl ---
// --- begin: tile_light_loop.glsl ---
// Tile-binned light loop shared between the forward (transparent / liquid)
// pipeline and the deferred batched-lighting pass. Returns ONLY the direct
// PBR contribution from tile-assigned lights; ambient is the caller's job.
//
// Compile-time variants:
//
//   NOODLE_TILE_SKIP_SHADOWED — set by the deferred batched-lighting pass.
//     Lights whose d0.w >= 4.0 (shadow flag set) are skipped before fetching
//     d1 — they're already handled by the per-light shadow passes elsewhere
//     in the deferred pipeline. Directional lights are always shadow-flagged
//     in this engine, so the directional branch is compiled out as well.
//
//   (default) — forward path. The shadow flag is stripped via mod() so
//     shadow-flagged lights still contribute. Directional lights take a
//     dedicated branch (no distance cull, CSM applied when NOODLE_SHADOW_CSM
//     is defined). Shadow point/spot lights run unshadowed in forward — the
//     forward pipeline has no per-light shadow pass.
//
// Light data layout (4 RGBA texels per light) matches Light.packGpuData:
//   d0 = position.xyz, type     (type += 4.0 when the light casts shadows)
//   d1 = color.rgb,    outerRange²
//   d2 = direction.xyz, _       (directional / spot only)
//   d3 = innerRange, outerRange, cos(innerCone), cos(outerCone)
//
// Caller is responsible for including pbr_lighting.glsl before this file —
// the deferred shader's per-light pass also needs it, so it stays at the
// caller scope to avoid double-inclusion.

uniform sampler2D uLightDataMap;
uniform int uLightCount;
uniform float uLightDataHeight;
uniform sampler2D uTileDataMap;
uniform int uTilesX;
uniform int uTileSize;

// Hard cap per tile — must match MAX_LIGHTS_PER_TILE in TileLightAssigner.ts.
// The tile data texture row stride is (1 + this), so changing it on one side
// without the other shifts the texelFetch indices and corrupts every lookup.
#define NOODLE_MAX_LIGHTS_PER_TILE 128

#if !defined(NOODLE_TILE_SKIP_SHADOWED) && defined(NOODLE_SHADOW_CSM)
// --- begin: csm_shadow.glsl ---
uniform sampler2D uShadowCascadeMap0;
uniform sampler2D uShadowCascadeMap1;
uniform sampler2D uShadowCascadeMap2;
uniform sampler2D uShadowCascadeMap3;
uniform mat4 uCascadeViewMatrix[4];
uniform vec4 uCascadeSplits;
uniform mat4 uViewMatrix;

// Positive-warp EVSM constant. Must match the value baked into the moments
// resolve shader (\`csm-moments-resolve-fragment.glsl\`) and the temporal
// blend shader (\`csm-moments-blend-fragment.glsl\`). 5.0 is the canonical
// value for fp32 moments — keeps the exponential within precision limits
// while damping the per-blur-kernel variance signal that c=7 was inflating.
// Higher c tightens Chebyshev at silhouettes but amplifies blur-kernel
// variance on glancing surfaces, producing visible per-shadow-texel
// quantization (square cells of differing shadow value) on near-vertical
// walls viewed at low sun. c=5 trades that off for slightly more bleed
// at shadow-on-shadow contacts.
//
// Future high-quality option: full EVSM with both positive and negative
// warps (RGBA32F, 2× memory, ~50% more resolve/blur/blend cost). Stores
// \`(M1+, M2+, M1-, M2-) = (exp(c·d), exp(2c·d), -exp(-c·d), exp(-2c·d))\`
// and takes \`min(chebyshev+, chebyshev-)\` — the negative bound is tight
// where the positive is loose and vice versa, almost completely
// eliminating shadow-on-shadow halos. Skipped for now to keep 60fps on
// mid-tier hardware; gate behind a high/ultra graphics preset if added.
#define C_EVSM 5.0

// Chebyshev variance floor — caps \`1/variance\` blow-up on near-constant
// regions. Tuned in the warped domain; the value is in "exp(c·d) units"
// squared so it scales with C_EVSM. 1e-4 is the canonical Lauritzen value
// scaled into the warped range.
#define MIN_VARIANCE 0.0001

// Light-bleed reduction crush. linstep pushes the lower tail of the
// Chebyshev bound to zero so faint glows under arches / behind silhouettes
// read as fully shadowed instead of "lit at p≈0.15". Tradeoff: also crushes
// genuine penumbra — 0.2 is a moderate setting; bump to 0.3 if Sponza shows
// visible bleed under arches.
#define LIGHT_BLEED_THRESHOLD 0.3

// Slope-scaled receiver bias. On surfaces grazing the light (NdotL near 0)
// the moments blur kernel mixes shadow-map texels with widely different
// recorded depths — \`M2 - M1²\` becomes large enough that Chebyshev returns
// per-texel intermediate p values, then \`linstep(LIGHT_BLEED_THRESHOLD, 1.0, p)\`
// crushes those into visible cell boundaries on the receiver wall. This
// pushes the warped receiver depth toward the light proportionally to
// \`(1 - NdotL)\`, so grazing receivers fall clearly below the moment mean
// (Chebyshev's early-out → 1.0) instead of grazing the variance threshold.
//
// Value is in NDC-z units; with cascade 0's ~115m Z-range, 0.01 ≈ ~115cm
// world detach at the most-grazing case. Surfaces facing the sun directly
// (NdotL=1) get no bias at all, so player shadows on flat ground at high
// sun stay attached. The artifact is most visible at low sun where many
// surfaces are grazing — those same surfaces benefit most from the bias.
// Smaller values (0.002-0.004) leave residual cell artifacts on extremely
// grazing walls; 0.01 is large enough to clear the moments-kernel variance
// range across all cascades while keeping the most-grazing detach under 1m
// (acceptable since players don't notice missing shadows on near-vertical
// surfaces — only on flat ground where bias is zero).
#define SLOPE_BIAS_SCALE 0.01

float linstep(float lo, float hi, float v) {
	return clamp((v - lo) / (hi - lo), 0.0, 1.0);
}

float chebyshev(vec2 moments, float warpedReceiver) {
	if (warpedReceiver <= moments.x) return 1.0;
	float variance = max(moments.y - moments.x * moments.x, MIN_VARIANCE);
	float diff = warpedReceiver - moments.x;
	float pMax = variance / (variance + diff * diff);
	return linstep(LIGHT_BLEED_THRESHOLD, 1.0, pMax);
}

float sample_cascade(sampler2D cascadeMap, vec3 worldPosition, mat4 cascadeMatrix, float slopeBias) {
	vec4 shadowCoord = cascadeMatrix * vec4(worldPosition, 1.0);
	vec3 projectedCoord = shadowCoord.xyz / shadowCoord.w;
	projectedCoord = projectedCoord * 0.5 + 0.5;

	if (projectedCoord.x < 0.0 || projectedCoord.x > 1.0 ||
		projectedCoord.y < 0.0 || projectedCoord.y > 1.0) {
		return 1.0;
	}

	// Hardware bilinear lookup on the pre-blurred moments texture — replaces
	// the 4-tap Poisson PCF kernel. Blurring in moments space (separable
	// Gaussian, applied after resolve) preserves Chebyshev's bound, so the
	// soft-shadow look comes for free without runtime kernel taps.
	vec2 moments = texture(cascadeMap, projectedCoord.xy).rg;
	float warpedReceiver = exp(C_EVSM * (projectedCoord.z - slopeBias));
	return chebyshev(moments, warpedReceiver);
}

float compute_shadow_csm(vec3 worldPosition, float NdotL) {
	float slopeBias = SLOPE_BIAS_SCALE * (1.0 - clamp(NdotL, 0.0, 1.0));
	float viewDepth = (uViewMatrix * vec4(worldPosition, 1.0)).z;

	// Long-shadow overlay: cascade 3 covers mountain-scale casters that the
	// near cascades' tight ortho frustums clip out. Without this, a receiver
	// in cascade 0/1's range under a distant mountain reads "no caster
	// recorded → lit" and the mountain shadow simply disappears as you walk
	// closer. min() against the local cascade keeps near-shadow precision
	// intact while picking up the long-range occluder. Cascade 3 returns 1.0
	// when the receiver projects outside its UV, so receivers far beyond the
	// far cascade fall back to the local result automatically.
	//
	// Cascade 3's per-texel ringing on grazing terrain that contaminated
	// every receiver before is now handled by the slope-scaled receiver
	// bias above (\`slopeBias\`); without that fix, sampling cascade 3
	// unconditionally would re-introduce the speckle.
	float longShadow = sample_cascade(uShadowCascadeMap3, worldPosition, uCascadeViewMatrix[3], slopeBias);

	// Determine cascade and blend between adjacent cascades near boundaries.
	// Inner blend zones use a fixed 5m to keep close-cascade transitions tight;
	// the 2→3 transition uses a wider zone because cascade 3 covers much larger
	// world distances (~2km) so a 5m blend would be invisible at that scale.
	float blendZone = 5.0;

	if (viewDepth > -uCascadeSplits.x) {
		float shadow0 = sample_cascade(uShadowCascadeMap0, worldPosition, uCascadeViewMatrix[0], slopeBias);
		// Blend near the boundary with cascade 1
		float distToBoundary = -viewDepth - (uCascadeSplits.x - blendZone);
		if (distToBoundary > 0.0) {
			float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
			return min(longShadow, mix(shadow0, shadow1, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow0);
	} else if (viewDepth > -uCascadeSplits.y) {
		float shadow1 = sample_cascade(uShadowCascadeMap1, worldPosition, uCascadeViewMatrix[1], slopeBias);
		// Blend near the boundary with cascade 2
		float distToBoundary = -viewDepth - (uCascadeSplits.y - blendZone);
		if (distToBoundary > 0.0) {
			float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
			return min(longShadow, mix(shadow1, shadow2, smoothstep(0.0, 1.0, distToBoundary / blendZone)));
		}
		return min(longShadow, shadow1);
	} else if (viewDepth > -uCascadeSplits.z) {
		float shadow2 = sample_cascade(uShadowCascadeMap2, worldPosition, uCascadeViewMatrix[2], slopeBias);
		// Blend near the boundary with cascade 3 (mountain-scale).
		float c23BlendZone = 25.0;
		float distToBoundary = -viewDepth - (uCascadeSplits.z - c23BlendZone);
		if (distToBoundary > 0.0) {
			return min(longShadow, mix(shadow2, longShadow, smoothstep(0.0, 1.0, distToBoundary / c23BlendZone)));
		}
		return min(longShadow, shadow2);
	} else if (viewDepth > -uCascadeSplits.w) {
		// Fade shadow out toward the cascade 3 far boundary so the transition to
		// the unshadowed beyond-CSM region is gradual rather than a hard line.
		// Wide fade zone matches the far-cascade scale.
		float fadeZone = 200.0;
		float distToFar = -viewDepth - (uCascadeSplits.w - fadeZone);
		if (distToFar > 0.0) {
			return mix(longShadow, 1.0, smoothstep(0.0, 1.0, distToFar / fadeZone));
		}
		return longShadow;
	}

	return 1.0; // beyond last cascade
}

// --- end: csm_shadow.glsl ---
#endif

vec3 compute_tile_lighting(vec3 position, vec3 normal, vec3 surfaceToCamera, float metallic, float roughness, vec3 albedo) {
	vec3 lighting = vec3(0.0);

	ivec2 tileCoord = ivec2(gl_FragCoord.xy) / uTileSize;
	int tileIndex = tileCoord.y * uTilesX + tileCoord.x;
	int tileCount = min(int(texelFetch(uTileDataMap, ivec2(0, tileIndex), 0).r), NOODLE_MAX_LIGHTS_PER_TILE);

	for (int t = 0; t < tileCount; t++) {
		int i = int(texelFetch(uTileDataMap, ivec2(t + 1, tileIndex), 0).r);
		vec4 d0 = texelFetch(uLightDataMap, ivec2(0, i), 0);

#ifdef NOODLE_TILE_SKIP_SHADOWED
		// Shadow-casting lights are handled by the per-light shadow passes;
		// skip before d1 fetch to save a texelFetch per culled light.
		if (d0.w >= 4.0) continue;
		float lightType = d0.w;
#else
		// Strip the shadow flag so shadow-flagged lights still contribute.
		// Only the directional CSM branch below applies an actual shadow
		// factor — point/spot shadow lights run unshadowed in forward.
		float lightType = mod(d0.w, 4.0);
#endif

		vec4 d1 = texelFetch(uLightDataMap, ivec2(1, i), 0);

		vec3 lightPos = d0.xyz;
		vec3 delta = lightPos - position;
		float dist2 = dot(delta, delta);
		float outerRange2 = d1.w;
#ifdef NOODLE_TILE_SKIP_SHADOWED
		if (dist2 > outerRange2) continue;
#else
		// Directional has effectively infinite range — keep it.
		if (lightType != 1.0 && dist2 > outerRange2) continue;
#endif

		vec3 lightColor = d1.rgb;
		vec3 surfaceToLight;
		float attenuation = 1.0;
		float shadow = 1.0;
		float NdotL;

#ifndef NOODLE_TILE_SKIP_SHADOWED
		if (lightType == 1.0) {
			vec4 d2 = texelFetch(uLightDataMap, ivec2(2, i), 0);
			surfaceToLight = -normalize(d2.xyz);
			NdotL = dot(normal, surfaceToLight);
			if (NdotL <= 0.0) continue;
#ifdef NOODLE_SHADOW_CSM
			shadow = compute_shadow_csm(position, NdotL);
#endif
		} else
#endif
		{
			surfaceToLight = normalize(delta);
			NdotL = dot(normal, surfaceToLight);
			if (NdotL <= 0.0) continue;

			float dist = sqrt(dist2);
			vec4 d3 = texelFetch(uLightDataMap, ivec2(3, i), 0);
			float innerRange = d3.x;
			float outerRange = d3.y;
			attenuation = smoothstep(outerRange, innerRange, dist);

			if (lightType == 2.0) {
				vec4 d2 = texelFetch(uLightDataMap, ivec2(2, i), 0);
				// surfaceToLight = normalize(lightPos - position), so the
				// surface-from-light direction is -surfaceToLight — already
				// normalized and in scope, no need to renormalize delta.
				float dotFromDir = dot(-surfaceToLight, normalize(d2.xyz));
				attenuation = min(attenuation, smoothstep(d3.w, d3.z, dotFromDir));
			}
		}

		lighting += shadow * attenuation * pbr_direct_lighting_ndotl(normal, surfaceToLight, surfaceToCamera, albedo, metallic, roughness, lightColor, NdotL);
	}

	return lighting;
}

// --- end: tile_light_loop.glsl ---

#ifdef NOODLE_NORMAL_MAP
// --- begin: normal_map.glsl ---
uniform sampler2D uNormalMapSampler;

vec3 perturb_normal(vec3 N, vec3 worldPos, vec2 texCoord) {
    vec3 dp1 = dFdx(worldPos);
    vec3 dp2 = dFdy(worldPos);
    vec2 duv1 = dFdx(texCoord);
    vec2 duv2 = dFdy(texCoord);

    vec3 dp2perp = cross(dp2, N);
    vec3 dp1perp = cross(N, dp1);

    vec3 T = dp2perp * duv1.x + dp1perp * duv2.x;
    vec3 B = dp2perp * duv1.y + dp1perp * duv2.y;

    float invmax = inversesqrt(max(dot(T, T), dot(B, B)));
    mat3 TBN = mat3(T * invmax, B * invmax, N);

    vec3 mapN = texture(uNormalMapSampler, texCoord).rgb * 2.0 - 1.0;
    return normalize(TBN * mapN);
}

// --- end: normal_map.glsl ---
#endif

#ifdef NOODLE_METALLIC_ROUGHNESS
uniform sampler2D uMetallicRoughnessSampler;
#endif

void main(void) {
	vec4 albedo = vec4(1.0);

#ifdef NOODLE_TEXTURE
	albedo = texture(uTextureSampler, vTextureCoord);
	// Scale alpha cutoff with mip level: low threshold up close, higher at distance
	// where mipmap blending smears transparent texels into the opaque ones
	float mipScale = clamp(length(fwidth(vTextureCoord)) * 30.0, 0.0, 1.0);
	float alphaCutoff = mix(0.01, 0.5, mipScale);
	if (albedo.a < alphaCutoff) discard;
#endif

#ifdef NOODLE_PRESET_COLOR
	albedo *= uPresetColor;
#endif

	vec3 normal = normalize(vWorldNormal);
	if (!gl_FrontFacing) normal = -normal;
#ifdef NOODLE_NORMAL_MAP
	normal = perturb_normal(normal, vWorldPosition.xyz, vTextureCoord);
#endif
	vec3 position = vWorldPosition.xyz;
	vec3 surfaceToCamera = normalize(uCameraPosition - position);

	float metallic = 0.0;
	float roughness = 0.5;
#ifdef NOODLE_METALLIC_ROUGHNESS
	vec4 mr = texture(uMetallicRoughnessSampler, vTextureCoord);
	roughness = mr.g;
	metallic = mr.b;
#endif

	vec3 lighting = uAmbientColor * albedo.rgb + compute_tile_lighting(position, normal, surfaceToCamera, metallic, roughness, albedo.rgb);

#ifdef NOODLE_VOLUMETRIC
	lighting += texture(uVolumetricMap, gl_FragCoord.xy / uScreenSize).rgb;
#endif

	fragColor = vec4(lighting * uOpacity, albedo.a * uOpacity);
}
`;

    private static vertexShaderSource = `
uniform mat4 uModelViewMatrix;
uniform mat4 uProjectionMatrix;
uniform mat4 uModelMatrix;

layout(location = 0) in vec3 aVertexPosition;
layout(location = 1) in vec3 aVertexNormal;

out vec4 vWorldPosition;
out vec3 vWorldNormal;

#ifdef NOODLE_TEXTURE
layout(location = 2) in vec2 aTextureCoord;
out vec2 vTextureCoord;
#endif

#ifdef NOODLE_ANIMATION
// --- begin: animation.glsl ---
layout(location = 4) in vec4 aJointWeights;
layout(location = 5) in vec4 aJointIndices;

uniform mat4 uBindShapeMatrix;
uniform sampler2D uMatrixSampler;
uniform vec3 uDataSettings;
uniform vec4 uBlendSettings;

#ifdef NOODLE_BONE_OVERRIDES
uniform mat4 uBoneOverrides[32];
uniform int uBoneOverrideIndices[32];
uniform int uBoneOverrideCount;
#endif

mat4 read_matrix(sampler2D tex, float i, vec2 size) {
	const float offset = 0.5;
	float tx = mod(i * 4.0, size.x) + offset;
	float ty = floor(i * 4.0 / size.x) + offset;

	return mat4(
		texture(tex, vec2((tx + 0.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 1.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 2.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 3.0) / size.x, ty / size.y))
	);
}

mat4 read_animation() {
	mat4 matrix = mat4(0.0);
	for (int j = 0; j < 4; j++) {
		float w = aJointWeights[j];
		if (w > 0.0) {
			float jointIndex = float(aJointIndices[j]);
			float index = jointIndex + uDataSettings.z;
			mat4 jointMatrix = read_matrix(uMatrixSampler, index, uDataSettings.xy);

			// Blend with previous clip if active
			if (uBlendSettings.w > 0.0) {
				mat4 prevMatrix = read_matrix(uMatrixSampler, jointIndex + uBlendSettings.z, uBlendSettings.xy);
				jointMatrix = prevMatrix + (jointMatrix - prevMatrix) * uBlendSettings.w;
			}

			#ifdef NOODLE_BONE_OVERRIDES
			for (int k = 0; k < uBoneOverrideCount; k++) {
				if (uBoneOverrideIndices[k] == int(jointIndex)) {
					jointMatrix = uBoneOverrides[k];
					break;
				}
			}
			#endif

			matrix += jointMatrix * w;
		}
	}
	return matrix;
}

// --- end: animation.glsl ---
#endif

#ifdef NOODLE_ATTACHMENT
uniform mat4 uBoneMatrix;
#endif

void main(void) {

#ifdef NOODLE_ANIMATION
	mat4 animationMatrix = read_animation();
	vec4 pos = vec4(aVertexPosition, 1.0) * uBindShapeMatrix * animationMatrix;
	vec4 normal = vec4(aVertexNormal, 0.0) * uBindShapeMatrix * animationMatrix;
#else
	vec4 pos = vec4(aVertexPosition, 1.0);
	vec4 normal = vec4(aVertexNormal, 0.0);
#endif

#ifdef NOODLE_ATTACHMENT
	pos = pos * uBoneMatrix;
	normal = normal * uBoneMatrix;
#endif

#ifdef NOODLE_TEXTURE
	vTextureCoord = aTextureCoord;
#endif

	vWorldPosition = uModelMatrix * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(uModelMatrix) * normal.xyz);

	vec4 viewPos = uModelViewMatrix * vec4(pos.xyz, 1.0);
	gl_Position = uProjectionMatrix * viewPos;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: ForwardTransparentShaderUniformsContainer;
    public attributesContainer: ForwardTransparentShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: ForwardTransparentShaderSettings) {
        super(ctx, ForwardTransparentShader.fragmentShaderSource, ForwardTransparentShader.vertexShaderSource, settings);
        this.uniformsContainer = new ForwardTransparentShaderUniformsContainer(ctx, this);
        this.attributesContainer = new ForwardTransparentShaderShaderAttributesContainer(ctx, this);
    }
}

class ForwardTransparentShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: ForwardTransparentShader) {
        super(ctx);
    }

    public setModelViewMatrix(value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setProjectionMatrix(value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setModelMatrix(value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setBindShapeMatrix(value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uBindShapeMatrix', this.shader.uniforms.uBindShapeMatrix, value, 'mat4');
        return this;
    }

    public setMatrixSampler(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uMatrixSampler', this.shader.uniforms.uMatrixSampler, texture, 'sampler2D');
        return this;
    }

    public setDataSettings(value: vec3 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uDataSettings', this.shader.uniforms.uDataSettings, value, 'vec3');
        return this;
    }

    public setBlendSettings(value: vec4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uBlendSettings', this.shader.uniforms.uBlendSettings, value, 'vec4');
        return this;
    }

    public setBoneOverrides(index: number, value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrides: ' + index);
        this.setValue('uBoneOverrides', this.shader.uniforms.uBoneOverrides[index], value, 'mat4');
        return this;
    }

    public setBoneOverrideIndices(index: number, value: number) : ForwardTransparentShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrideIndices: ' + index);
        this.setValue('uBoneOverrideIndices', this.shader.uniforms.uBoneOverrideIndices[index], value, 'int');
        return this;
    }

    public setBoneOverrideCount(value: number) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uBoneOverrideCount', this.shader.uniforms.uBoneOverrideCount, value, 'int');
        return this;
    }

    public setBoneMatrix(value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uBoneMatrix', this.shader.uniforms.uBoneMatrix, value, 'mat4');
        return this;
    }

    public setTextureSampler(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uTextureSampler', this.shader.uniforms.uTextureSampler, texture, 'sampler2D');
        return this;
    }

    public setPresetColor(value: vec4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uPresetColor', this.shader.uniforms.uPresetColor, value, 'vec4');
        return this;
    }

    public setAmbientColor(value: vec3 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uAmbientColor', this.shader.uniforms.uAmbientColor, value, 'vec3');
        return this;
    }

    public setCameraPosition(value: vec3 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uCameraPosition', this.shader.uniforms.uCameraPosition, value, 'vec3');
        return this;
    }

    public setOpacity(value: number) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uOpacity', this.shader.uniforms.uOpacity, value, 'float');
        return this;
    }

    public setVolumetricMap(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uVolumetricMap', this.shader.uniforms.uVolumetricMap, texture, 'sampler2D');
        return this;
    }

    public setScreenSize(value: vec2 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uScreenSize', this.shader.uniforms.uScreenSize, value, 'vec2');
        return this;
    }

    public setLightDataMap(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uLightDataMap', this.shader.uniforms.uLightDataMap, texture, 'sampler2D');
        return this;
    }

    public setLightCount(value: number) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uLightCount', this.shader.uniforms.uLightCount, value, 'int');
        return this;
    }

    public setLightDataHeight(value: number) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uLightDataHeight', this.shader.uniforms.uLightDataHeight, value, 'float');
        return this;
    }

    public setTileDataMap(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uTileDataMap', this.shader.uniforms.uTileDataMap, texture, 'sampler2D');
        return this;
    }

    public setTilesX(value: number) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uTilesX', this.shader.uniforms.uTilesX, value, 'int');
        return this;
    }

    public setTileSize(value: number) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uTileSize', this.shader.uniforms.uTileSize, value, 'int');
        return this;
    }

    public setShadowCascadeMap0(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap0', this.shader.uniforms.uShadowCascadeMap0, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap1(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap1', this.shader.uniforms.uShadowCascadeMap1, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap2(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap2', this.shader.uniforms.uShadowCascadeMap2, texture, 'sampler2D');
        return this;
    }

    public setShadowCascadeMap3(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uShadowCascadeMap3', this.shader.uniforms.uShadowCascadeMap3, texture, 'sampler2D');
        return this;
    }

    public setCascadeViewMatrix(index: number, value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        if(index < 0 || index >= 4) throw new Error('Invalid index used for uCascadeViewMatrix: ' + index);
        this.setValue('uCascadeViewMatrix', this.shader.uniforms.uCascadeViewMatrix[index], value, 'mat4');
        return this;
    }

    public setCascadeSplits(value: vec4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uCascadeSplits', this.shader.uniforms.uCascadeSplits, value, 'vec4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : ForwardTransparentShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setNormalMapSampler(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uNormalMapSampler', this.shader.uniforms.uNormalMapSampler, texture, 'sampler2D');
        return this;
    }

    public setMetallicRoughnessSampler(texture: Texture) : ForwardTransparentShaderUniformsContainer {
        this.setTextureValue('uMetallicRoughnessSampler', this.shader.uniforms.uMetallicRoughnessSampler, texture, 'sampler2D');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uMatrixSampler', uniform: this.shader.uniforms.uMatrixSampler, dataType: 'sampler2D' },
			{ name: 'uTextureSampler', uniform: this.shader.uniforms.uTextureSampler, dataType: 'sampler2D' },
			{ name: 'uVolumetricMap', uniform: this.shader.uniforms.uVolumetricMap, dataType: 'sampler2D' },
			{ name: 'uLightDataMap', uniform: this.shader.uniforms.uLightDataMap, dataType: 'sampler2D' },
			{ name: 'uTileDataMap', uniform: this.shader.uniforms.uTileDataMap, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap0', uniform: this.shader.uniforms.uShadowCascadeMap0, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap1', uniform: this.shader.uniforms.uShadowCascadeMap1, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap2', uniform: this.shader.uniforms.uShadowCascadeMap2, dataType: 'sampler2D' },
			{ name: 'uShadowCascadeMap3', uniform: this.shader.uniforms.uShadowCascadeMap3, dataType: 'sampler2D' },
			{ name: 'uNormalMapSampler', uniform: this.shader.uniforms.uNormalMapSampler, dataType: 'sampler2D' },
			{ name: 'uMetallicRoughnessSampler', uniform: this.shader.uniforms.uMetallicRoughnessSampler, dataType: 'sampler2D' }
        ];
    }
}

class ForwardTransparentShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: ForwardTransparentShader) {
        super(ctx);
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexNormal(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setTextureCoord(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointWeights(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointWeights, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointIndices(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointIndices, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPosition(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldNormal(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTextureCoord(settings: { length: number, offset: number, itemSize: number }) : ForwardTransparentShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}